import { useState, type ReactNode } from "react";
import { homePathFor, useAuth } from "../auth/AuthContext";
import { AppFooter } from "../components/AppFooter";
import { BrandLogo, IconBuilding, IconClipboard, IconDumbbell, IconGroup, IconHelp, IconHome, IconInbox, IconMenu, IconRunner, IconShield, IconUser, IconWhistle } from "../components/icons";
import { NotificationBell } from "../components/NotificationBell";
import { ThemeToggle } from "../components/ThemeToggle";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { ROLE_LABEL } from "../lib/format";
import { useSupportUnread } from "../lib/useSupportUnread";
import { Link, navigate, useLocation } from "../router/router";
import { t, tn } from "../i18n";

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  exact?: boolean;
  badge?: number;
}

export function ManagementLayout({ children }: { children: ReactNode }) {
  const { me, membership, can, selectOrganization, logout } = useAuth();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const supportUnread = useSupportUnread(!!me?.is_platform_admin);
  if (!me) return null;

  const orgBase = membership ? `/org/${membership.organization_id}` : null;
  const showBell = !!membership && can("notifications.read") && membership.roles.some((r) => r !== "athlete");
  const orgItems: NavItem[] = [];
  if (orgBase && membership && (membership.roles.includes("org_manager") || membership.roles.includes("coach"))) {
    orgItems.push({ to: orgBase, label: t("Visão geral"), icon: <IconHome />, exact: true });
    if (can("athletes.read")) orgItems.push({ to: `${orgBase}/atletas`, label: t("Atletas"), icon: <IconRunner /> });
    if (can("templates.manage")) orgItems.push({ to: `${orgBase}/modelos`, label: t("Modelos de treino"), icon: <IconClipboard /> });
    if (can("exercises.manage")) orgItems.push({ to: `${orgBase}/exercicios`, label: t("Exercícios de força"), icon: <IconDumbbell /> });
    if (can("coaches.read")) orgItems.push({ to: `${orgBase}/treinadores`, label: t("Treinadores"), icon: <IconWhistle /> });
    if (can("groups.read")) orgItems.push({ to: `${orgBase}/grupos`, label: t("Grupos"), icon: <IconGroup /> });
    if (can("audit.read")) orgItems.push({ to: `${orgBase}/auditoria`, label: t("Auditoria"), icon: <IconShield /> });
    orgItems.push({ to: `${orgBase}/ajuda`, label: t("Ajuda"), icon: <IconHelp /> });
  }
  const adminItems: NavItem[] = me.is_platform_admin
    ? [
        { to: "/admin/assessorias", label: t("Assessorias"), icon: <IconBuilding /> },
        { to: "/admin/auditoria", label: t("Auditoria global"), icon: <IconShield /> },
        { to: "/admin/suporte", label: t("Suporte"), icon: <IconInbox />, badge: supportUnread },
        { to: "/admin/ajuda", label: t("Ajuda"), icon: <IconHelp /> },
      ]
    : [];

  const isActive = (item: NavItem) =>
    item.exact ? pathname === item.to : pathname === item.to || pathname.startsWith(`${item.to}/`);

  const renderItems = (items: NavItem[]) =>
    items.map((item) => (
      <Link
        key={item.to}
        to={item.to}
        aria-current={isActive(item) ? "page" : undefined}
        onClick={() => setOpen(false)}
      >
        {item.icon}
        {item.label}
        {!!item.badge && (
          <span className="nav-badge" aria-label={tn("{n} aguardando resposta", "{n} aguardando resposta", item.badge)}>
            {item.badge > 99 ? "99+" : item.badge}
          </span>
        )}
      </Link>
    ));

  return (
    <div className="shell">
      <a href="#conteudo" className="skip-link">
        {t("Ir para o conteúdo")}
      </a>
      <div className="topbar">
        <Link to="/" className="brand" style={{ padding: 0 }}>
          <BrandLogo />
        </Link>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          {showBell && membership && <NotificationBell orgId={membership.organization_id} mode="staff" />}
          <button className="btn btn-ghost btn-sm" onClick={() => setOpen(true)} aria-label={t("Abrir menu")} aria-expanded={open}>
            <IconMenu width={22} height={22} />
          </button>
        </div>
      </div>
      {open && <div className="scrim" onClick={() => setOpen(false)} aria-hidden />}

      <aside className={`sidebar ${open ? "open" : ""}`} aria-label={t("Navegação principal")}>
        <Link to="/" className="brand" onClick={() => setOpen(false)}>
          <BrandLogo />
        </Link>

        {membership && (
          <div className="org-switch">
            <label htmlFor="org-select">{t("Assessoria")}</label>
            {me.organizations.length > 1 ? (
              <select
                id="org-select"
                value={membership.organization_id}
                onChange={(e) => {
                  selectOrganization(e.target.value);
                  const target = me.organizations.find((o) => o.organization_id === e.target.value) ?? null;
                  navigate(homePathFor(me, target));
                }}
              >
                {me.organizations.map((o) => (
                  <option key={o.organization_id} value={o.organization_id}>
                    {o.organization_name}
                  </option>
                ))}
              </select>
            ) : (
              <div className="org-name" id="org-select">
                {membership.organization_name}
              </div>
            )}
          </div>
        )}

        <nav className="nav" aria-label={t("Seções")}>
          {renderItems(orgItems)}
          {membership?.roles.includes("athlete") && (
            <Link to={`/org/${membership.organization_id}/atleta`} onClick={() => setOpen(false)}>
              <IconUser />
              {t("Minha área de atleta")}
            </Link>
          )}
          {adminItems.length > 0 && (
            <>
              <div className="nav-group-label">{t("Plataforma")}</div>
              {renderItems(adminItems)}
            </>
          )}
        </nav>

        <div className="sidebar-foot">
          <div>
            <div className="who">{me.full_name}</div>
            <div className="role">
              {membership
                ? membership.roles.map((r) => ROLE_LABEL[r]).join(t(" e "))
                : me.is_platform_admin
                  ? t("Administração da plataforma")
                  : ""}
            </div>
          </div>
          <div className="row">
            {showBell && membership && (
              <span className="bell-desktop">
                <NotificationBell orgId={membership.organization_id} mode="staff" placement="up" />
              </span>
            )}
            <Link to="/conta" className="btn btn-ghost btn-sm" onClick={() => setOpen(false)}>
              {t("Minha conta")}
            </Link>
            <LanguageSwitcher loggedIn compact />
            <ThemeToggle />
            <button className="btn btn-ghost btn-sm" onClick={logout}>
              {t("Sair")}
            </button>
          </div>
        </div>
      </aside>

      <main className="main" id="conteudo" tabIndex={-1}>
        <div className="main-inner">{children}</div>
        <AppFooter helpTo={membership ? `/org/${membership.organization_id}/ajuda` : me.is_platform_admin ? "/admin/ajuda" : undefined} />
      </main>
    </div>
  );
}
